/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box } from "@mui/material";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { useTenantTranslation } from "hooks/formatters";
import { RecentSearches } from "./RecentSearches";
import { CurrentlyDeployedPlan } from "./CurrentlyDeployedPlan";
import { LatestInventory } from "./LatestInventoryReport";
import { CurrentInventoryEstimate } from "./CurrentInventoryEstimate";
import { HeatsOfTheDay } from "./HeatsOfTheDay";
import { CurrentRecipe } from "./CurrentRecipe";
import dayjs from "dayjs";
import {
  RequireFeatureFlags,
  RequirePermissions,
} from "src/routes/authentication";
import { DateStepper } from "components/DateStepper";
import { useDayjsDateStringParamState } from "hooks/useDayjsDateStringParamState";
import { useLocalDatetime } from "hooks/useLocalDateTime";
import { ConsumptionProjection } from "./ConsumptionProjection";
import { Production } from "./Production";
import { Deliveries } from "./Deliveries";
import { Compliance } from "./Compliance";
import { LiveRecipe } from "./LiveRecipe";

export const OverviewPage = () => {
  const { localTimezone } = useLocalDatetime();

  const { t } = useTenantTranslation();
  const [date, setDate] = useDayjsDateStringParamState(
    "date",
    dayjs().tz(localTimezone),
    localTimezone
  );

  return (
    <AccountPageLayout title={t("overview")}>
      <DateStepper date={date} setDate={setDate} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(500px, 1fr))",
          pt: 2,
          gap: 3,
        }}
      >
        <RequirePermissions
          permissions={["strategy_dashboard_production", "analytics_admin"]}
        >
          <RequireFeatureFlags
            featureFlags={["strategy_dashboard_production_pace"]}
          >
            <Production date={date} />
          </RequireFeatureFlags>
        </RequirePermissions>
        <RequirePermissions
          permissions={[
            "strategy_dashboard_consumption_projection",
            "analytics_admin",
          ]}
        >
          <RequireFeatureFlags
            featureFlags={["strategy_dashboard_consumption_projection"]}
          >
            <ConsumptionProjection date={date} />
          </RequireFeatureFlags>
        </RequirePermissions>
        <RequirePermissions
          permissions={["strategy_dashboard_deliveries", "analytics_admin"]}
        >
          <RequireFeatureFlags featureFlags={["strategy_dashboard_deliveries"]}>
            <Deliveries date={date} />
          </RequireFeatureFlags>
        </RequirePermissions>
        <RequirePermissions
          permissions={["strategy_dashboard_compliance", "analytics_admin"]}
        >
          <RequireFeatureFlags featureFlags={["overview_compliance"]}>
            <Compliance date={date} />
          </RequireFeatureFlags>
        </RequirePermissions>

        <RequireFeatureFlags featureFlags={["overview_current_recipe"]}>
          <RequirePermissions
            permissions={["crane_supervisor", "crane_supervisor_viewer"]}
          >
            <CurrentRecipe />
          </RequirePermissions>
        </RequireFeatureFlags>
        <RequirePermissions
          permissions={["strategy_dashboard_heats", "analytics_admin"]}
        >
          <RequireFeatureFlags featureFlags={["strategy_dashboard_heats"]}>
            <HeatsOfTheDay date={date} />
          </RequireFeatureFlags>
        </RequirePermissions>
        <RequirePermissions
          permissions={["strategy_dashboard_inventory", "analytics_admin"]}
        >
          <RequireFeatureFlags featureFlags={["strategy_dashboard_inventory"]}>
            <LatestInventory timestamp={date} />
          </RequireFeatureFlags>
        </RequirePermissions>
        <RequirePermissions permissions={["read"]}>
          <RequireFeatureFlags featureFlags={["overview_deployed_plan"]}>
            <CurrentlyDeployedPlan />
          </RequireFeatureFlags>
          <RequireFeatureFlags featureFlags={["overview_recent_searches"]}>
            <RecentSearches />
          </RequireFeatureFlags>
        </RequirePermissions>

        <RequirePermissions
          permissions={["strategy_dashboard_inventory", "analytics_admin"]}
        >
          <RequireFeatureFlags
            featureFlags={[
              "inventory_estimate_at_timestamp",
              "strategy_dashboard_inventory",
            ]}
          >
            <CurrentInventoryEstimate timestamp={date?.toISOString() ?? ""} />
          </RequireFeatureFlags>
        </RequirePermissions>
        <RequirePermissions permissions={["read"]}>
          <RequireFeatureFlags featureFlags={["live_recipe_card"]}>
            <LiveRecipe />
          </RequireFeatureFlags>
        </RequirePermissions>
      </Box>
    </AccountPageLayout>
  );
};
